import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Navigation, Instagram, Facebook, MapPin, Star, ArrowRight } from 'lucide-react';
import { SITE, NAV } from '../../data/site';

const SOCIAL_LINKS = [
  { href: 'https://www.instagram.com/', label: 'Instagram', Icon: Instagram },
  { href: 'https://www.facebook.com/', label: 'Facebook', Icon: Facebook },
  { href: 'https://www.yelp.com/', label: 'Yelp', Icon: Star },
];

const SocialLinks = () => (
  <div className="flex items-center gap-2.5">
    {SOCIAL_LINKS.map(({ href, label, Icon }) => (
      <a
        key={label}
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        className="w-10 h-10 rounded-full border border-dark-foreground/15 text-dark-foreground/80 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-primary-foreground transition-colors duration-200"
      >
        <Icon className="w-[18px] h-[18px]" />
      </a>
    ))}
  </div>
);

// Call-to-action band above the footer columns.
const FooterCtaBar = () => (
  <div className="relative overflow-hidden bg-dark">
    <div
      className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl pointer-events-none"
      style={{ background: 'var(--gradient-fire)' }}
    />
    <div className="relative max-w-[1320px] mx-auto px-4 lg:px-8 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-7 border-b border-dark-foreground/10">
      <div>
        <p className="text-dark-foreground font-heading font-extrabold text-2xl md:text-3xl tracking-tight" data-testid="footer-cta-text">
          You say it, we make it!
        </p>
        <p className="text-dark-foreground/70 mt-2 font-body">
          Call <strong className="text-dark-foreground font-semibold">Edison</strong> directly on{' '}
          <a href={SITE.phoneHref} className="text-[hsl(var(--primary-glow))] font-semibold underline-offset-4 hover:underline">{SITE.phone}</a>
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <SocialLinks />
        <Link to="/free-estimate" className="btn-cta" data-testid="footer-free-estimate-btn">
          Free Estimate <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const BrandColumn = () => (
  <div className="flex flex-col items-start">
    <img src={SITE.logoWhite} alt="Phoenix Roofing & Chimney" className="h-14 w-auto" />
    <p className="font-body text-dark-foreground/70 mt-5 max-w-xs">
      Top-Rated Roofing & Chimney Contractor in New Jersey. Licensed, insured, and locally owned.
    </p>
    <Link to="/locations/new-jersey" className="btn-ghost-light mt-6 !py-3 !px-5" data-testid="footer-locations-btn">
      <MapPin className="w-4 h-4" /> Locations
    </Link>
  </div>
);

const FooterColumn = ({ title, children }) => (
  <div>
    <h3 className="font-heading font-bold text-base text-dark-foreground tracking-wide">{title}</h3>
    <div className="w-9 h-[3px] rounded-full mt-3 mb-6" style={{ background: 'var(--gradient-fire)' }} />
    <ul className="space-y-3.5 font-body text-[15px] text-dark-foreground/70">{children}</ul>
  </div>
);

const ContactColumn = () => (
  <FooterColumn title="Contact Us">
    <li className="flex items-start gap-3">
      <MapPin className="w-4 h-4 mt-1 shrink-0 text-[hsl(var(--primary-glow))]" />
      <a href={SITE.addressMapHref} target="_blank" rel="noreferrer" className="hover:text-dark-foreground transition-colors">{SITE.address}</a>
    </li>
    <li className="flex items-center gap-3">
      <Mail className="w-4 h-4 shrink-0 text-[hsl(var(--primary-glow))]" />
      <a href={`mailto:${SITE.email}`} className="hover:text-dark-foreground transition-colors break-all">{SITE.email}</a>
    </li>
    <li className="flex items-center gap-3">
      <Phone className="w-4 h-4 shrink-0 text-[hsl(var(--primary-glow))]" />
      <a href={SITE.phoneHref} className="hover:text-dark-foreground transition-colors">{SITE.phone}</a>
    </li>
    <li className="flex items-center gap-3">
      <Navigation className="w-4 h-4 shrink-0 text-[hsl(var(--primary-glow))]" />New Jersey
    </li>
  </FooterColumn>
);

const NavColumn = ({ title, items }) => (
  <FooterColumn title={title}>
    {items.map((i) => (
      <li key={i.label}>
        <Link to={i.path} className="hover:text-dark-foreground hover:translate-x-0.5 inline-block transition-all duration-200">{i.label}</Link>
      </li>
    ))}
  </FooterColumn>
);

export const Footer = () => (
  <footer data-testid="site-footer">
    <div className="bg-dark text-dark-foreground">
      <FooterCtaBar />
      <div className="max-w-[1320px] mx-auto px-4 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <BrandColumn />
        <ContactColumn />
        <NavColumn title="Roofing" items={NAV.roofing} />
        <NavColumn title="Chimney" items={NAV.chimney} />
      </div>
      <div className="border-t border-dark-foreground/10">
        <p className="text-center py-5 text-[13px] text-dark-foreground/55 font-body" data-testid="footer-copyright">
          Copyright © 2026 Phoenix Roofing & Chimney. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);
